import { getJson } from '@/lib/api';

/**
 * Targeted analytics — focused, tracked analyses that live beside the overall
 * dashboards. Each one is computed and cached daily by the API
 * (GET /api/v3/targeted-analytics), including an AI "what does this mean"
 * summary. Shapes mirror apps/api's targeted-analytics types.
 */

/** A device split GA4 reports. */
export type TargetedDevice = 'mobile' | 'desktop' | 'tablet';

/** One row in the list of available analyses (the cards page). */
export interface TargetedAnalysisCard {
  readonly slug: string;
  readonly title: string;
  readonly description: string;
  /** ISO timestamp of the last daily build, or null when never built. */
  readonly generatedAt: string | null;
}

/** A comparison window (e.g. before/after a change). */
export interface TargetedWindow {
  readonly key: string;
  readonly label: string;
  readonly start: string;
  readonly end: string;
}

/** One (window, page bucket, device) cell: the numerator, denominator and rate. */
export interface TargetedRateRow {
  readonly window: string;
  readonly page: string;
  readonly device: string;
  readonly sessions: number;
  readonly leads: number;
  /** leads / sessions, 0..1. */
  readonly rate: number;
}

/** The before/after device×page dataset (mobile-conversion). */
export interface WindowsDataset {
  readonly kind: 'windows';
  readonly windows: readonly TargetedWindow[];
  readonly rows: readonly TargetedRateRow[];
}

/** How a table column's values are rendered. */
export type TableColumnFormat = 'text' | 'int' | 'number' | 'currency' | 'percent';
/** A column in a generic table dataset. */
export interface TableColumn {
  readonly key: string;
  readonly label: string;
  readonly format?: TableColumnFormat;
}
/** A headline stat tile above a table dataset. */
export interface TableStat {
  readonly label: string;
  readonly value: string;
}
export type TableCell = string | number | null;
/** A generic headline + table dataset (e.g. ppc-cost-per-lead). */
export interface TableDataset {
  readonly kind: 'table';
  readonly headline: readonly TableStat[];
  readonly columns: readonly TableColumn[];
  readonly rows: readonly Readonly<Record<string, TableCell>>[];
}

/** The deterministic numbers behind an analysis, one of two shapes. */
export type TargetedDataset = WindowsDataset | TableDataset;

/** A fully-built analysis with its dataset and AI summary. */
export interface TargetedAnalysis {
  readonly slug: string;
  readonly title: string;
  readonly description: string;
  readonly generatedAt: string | null;
  readonly dataset: TargetedDataset;
  /** The AI narrative, or null when the model was unavailable at build time. */
  readonly summary: string | null;
  readonly summaryModel: string | null;
}

/** Lists the available analyses for the cards page. */
export function listTargetedAnalyses(signal?: AbortSignal): Promise<TargetedAnalysisCard[]> {
  return getJson<TargetedAnalysisCard[]>('/targeted-analytics', undefined, signal);
}

/** Reads one analysis (its dataset and cached AI summary). 404s until first built. */
export function readTargetedAnalysis(slug: string, signal?: AbortSignal): Promise<TargetedAnalysis> {
  return getJson<TargetedAnalysis>(`/targeted-analytics/${slug}`, undefined, signal);
}
